import { Predicate } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay } from 'rxjs/operators';

export type NavigationBackHandler = VoidFunction;
export type ComplexNavigationBackHandler = (defaultHandler: NavigationBackHandler) => void;

export class Activity {
    public readonly toolbar: Toolbar = new Toolbar();
    public readonly loader: Loader = new Loader();
    public onBack: ComplexNavigationBackHandler | null = null;

    public setupQuickly(title: string): void {
        this.reset();
        this.toolbar.setTitle(title);
    }

    public reset(): void {
        this.onBack = null;
        this.loader.hide();
        this.toolbar.reset();
    }
}

export class ActivityCommand {
    public icon: string = '';
    public title: string = '';
    public badge?: IBadge | null = null;
    public action: VoidFunction = () => {};
    public enabled: Predicate<void> = () => true;
}

export class Toolbar {
    private titleSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private visibilitySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private optionsSubject: BehaviorSubject<ActivityCommand[]> = new BehaviorSubject<ActivityCommand[]>([]);
    private advancedSubject: BehaviorSubject<ActivityCommand[]> = new BehaviorSubject<ActivityCommand[]>([]);
    public readonly title$: Observable<string | null> = this.titleSubject.pipe(delay(1));
    public readonly visible$: Observable<boolean> = this.visibilitySubject.pipe(delay(1));
    public readonly options$: Observable<ActivityCommand[]> = this.optionsSubject.pipe(delay(1));
    public readonly advanced$: Observable<ActivityCommand[]> = this.advancedSubject.pipe(delay(1));

    public setTitle(title: string | null): void {
        this.titleSubject.next(title);
    }

    public show(): void {
        this.visibilitySubject.next(true);
    }

    public hide(): void {
        this.visibilitySubject.next(false);
    }

    public setOptions(options: ActivityCommand[]): void {
        this.optionsSubject.next(options);
    }

    public setAdvanced(options: ActivityCommand[]): void {
        this.advancedSubject.next(options);
    }

    public reset() {
        this.show();
        this.setTitle(null);
        this.setOptions([]);
        this.setAdvanced([]);
    }
}

export class Loader {
    private titleSubject: Subject<string | null> = new BehaviorSubject<string | null>(null);
    private visibilitySubject: Subject<boolean> = new BehaviorSubject<boolean>(false);

    public readonly title$: Observable<string | null> = this.titleSubject.pipe(delay(1));
    public readonly visible$: Observable<boolean> = this.visibilitySubject.pipe(delay(1));

    show(title: string): void {
        this.titleSubject.next(title);
        this.visibilitySubject.next(true);
    }

    hide(): void {
        this.titleSubject.next(null);
        this.visibilitySubject.next(false);
    }
}

export interface IBadge {
    content: number;
    hidden: boolean;
}
